/**
 * Sistema de Permisos y Control de Acceso (RBAC)
 * NUEVO MODELO: admin_union, admin_association, club_director, member
 */

import type { SystemRole } from "@/types";

// ============================================
// DEFINICIÓN DE MÓDULOS Y ACCIONES
// ============================================

export type Module =
  | "dashboard"
  | "organization"
  | "members"
  | "progress"
  | "events"
  | "attendance"
  | "finance"
  | "reports"
  | "users"
  | "settings";

export type Action = "view" | "create" | "edit" | "delete" | "manage";

// ============================================
// MATRIZ DE PERMISOS POR ROL
// ============================================

/**
 * Define los permisos de cada rol para cada módulo
 * true = tiene acceso completo
 * array de acciones = acceso limitado
 * false/undefined = sin acceso
 *
 * MODELO DE NEGOCIO:
 * - admin_union: Gestiona asociaciones, usuarios, roles y ve estadísticas generales
 *   NO maneja: finanzas, eventos, asistencia, progreso (eso es nivel club)
 * - admin_association: Gestiona clubes de su asociación, ve miembros, asigna directores
 * - club_director: Gestiona TODO de su club (miembros, finanzas, eventos, etc.)
 */
const PERMISSIONS: Record<
  SystemRole,
  Partial<Record<Module, Action[] | boolean>>
> = {
  admin_union: {
    dashboard: true, // Ve estadísticas generales de todas las asociaciones
    organization: true, // Gestiona asociaciones (crear, editar, ver)
    members: ["view"], // Solo ve estadísticas de miembros, no gestiona
    progress: false, // No maneja progreso (es nivel club)
    events: false, // No maneja eventos (es nivel club)
    attendance: false, // No maneja asistencia (es nivel club)
    finance: false, // No maneja finanzas (es nivel club)
    reports: true, // Ve reportes/estadísticas generales
    users: true, // Gestiona usuarios (activar, ver)
    settings: true, // Configuración general
  },

  admin_association: {
    dashboard: true,
    organization: ["view", "create", "edit"], // Gestiona clubes de su asociación
    members: ["view"], // Ve miembros de su asociación
    progress: ["view"], // Ve progreso de su asociación
    events: ["view"], // Ve eventos de su asociación
    attendance: ["view"], // Ve asistencia de su asociación
    finance: ["view"], // Ve resumen financiero (opcional)
    reports: ["view"], // Reportes de su asociación
    users: ["view", "create", "edit"], // Puede asignar directores de club
    settings: ["view"],
  },

  club_director: {
    dashboard: true,
    organization: ["view"], // Solo ver su club
    members: true, // Gestión completa de miembros de SU club
    progress: true, // Gestión completa de progreso
    events: ["view", "create"], // Ver eventos y crear para su club
    attendance: true, // Gestión completa de asistencia
    finance: true, // Gestión de finanzas de su club
    reports: ["view"], // Reportes de su club
    users: false,
    settings: ["view"],
  },

  member: {
    dashboard: ["view"],
    organization: false,
    members: false, // Un miembro común no gestiona ni lista miembros del club
    progress: ["view"], // Ver su progreso
    events: ["view"],
    attendance: ["view"], // Ver su asistencia
    finance: false,
    reports: false,
    users: false,
    settings: false,
  },
};

// ============================================
// FUNCIONES DE VERIFICACIÓN
// ============================================

/**
 * Verifica si un rol tiene permiso para una acción en un módulo
 */
export function hasPermission(
  role: SystemRole | undefined,
  module: Module,
  action: Action = "view",
): boolean {
  if (!role) return false;

  const modulePermissions = PERMISSIONS[role]?.[module];

  if (modulePermissions === undefined || modulePermissions === false) {
    return false;
  }

  if (modulePermissions === true) {
    return true;
  }

  // Es un array de acciones permitidas
  return modulePermissions.includes(action);
}

/**
 * Verifica si un rol puede acceder a un módulo (cualquier acción)
 */
export function canAccessModule(
  role: SystemRole | undefined,
  module: Module,
): boolean {
  return hasPermission(role, module, "view");
}

/**
 * Verifica si un rol puede gestionar completamente un módulo
 */
export function canManageModule(
  role: SystemRole | undefined,
  module: Module,
): boolean {
  return (
    hasPermission(role, module, "manage") ||
    (hasPermission(role, module, "create") &&
      hasPermission(role, module, "edit") &&
      hasPermission(role, module, "delete"))
  );
}

/**
 * Obtiene todos los módulos accesibles para un rol
 */
export function getAccessibleModules(role: SystemRole | undefined): Module[] {
  if (!role) return [];

  const modules: Module[] = [
    "dashboard",
    "organization",
    "members",
    "progress",
    "events",
    "attendance",
    "finance",
    "reports",
    "users",
    "settings",
  ];

  return modules.filter((module) => canAccessModule(role, module));
}

// ============================================
// NIVELES DE SCOPE (JERARQUÍA)
// ============================================

export type ScopeLevel = "union" | "association" | "club";

/**
 * Mapea roles a su nivel de scope
 */
export function getRoleScopeLevel(role: SystemRole): ScopeLevel {
  switch (role) {
    case "admin_union":
      return "union";
    case "admin_association":
      return "association";
    case "club_director":
    case "member":
    default:
      return "club";
  }
}

/**
 * Verifica si un usuario puede ver datos de cierto nivel
 */
export function canViewScopeLevel(
  role: SystemRole,
  targetLevel: ScopeLevel,
): boolean {
  const hierarchy: ScopeLevel[] = ["union", "association", "club"];
  const userLevel = hierarchy.indexOf(getRoleScopeLevel(role));
  const target = hierarchy.indexOf(targetLevel);

  // Puede ver su nivel y los inferiores
  return userLevel <= target;
}

// ============================================
// ITEMS DE NAVEGACIÓN
// ============================================

export interface NavItem {
  name: string;
  href: string;
  icon: string;
  module: Module;
  /** Grupo base — se resuelve dinámicamente por rol con getNavGroupLabel() */
  group: string;
  /** Si se define, solo estos roles ven el item (además de tener acceso al module) */
  roles?: SystemRole[];
}

/**
 * Define los items de navegación con su módulo y grupo base.
 * El orden aquí define el orden en el menú.
 * "Equipo" fusiona Users + Roles en una sola entrada.
 */
export const NAV_ITEMS: NavItem[] = [
  {
    name: "Dashboard",
    href: "/dashboard",
    icon: "LayoutDashboard",
    module: "dashboard",
    group: "inicio",
  },
  {
    name: "Organización",
    href: "/organization",
    icon: "Building2",
    module: "organization",
    group: "organizacion",
  },
  {
    name: "Solicitudes de Clubes",
    href: "/organization/club-requests",
    icon: "Shield",
    module: "organization",
    group: "organizacion",
    roles: ["admin_association", "admin_union"],
  },
  {
    // Quien pide DIRIGIR un tipo de club también le sale al admin: nombrar a un
    // director no debería enterarse solo el club afectado.
    name: "Solicitudes de Dirección",
    href: "/organization/director-requests",
    icon: "ShieldCheck",
    module: "organization",
    group: "organizacion",
    roles: ["admin_association", "admin_union"],
  },
  {
    name: "Miembros",
    href: "/members",
    icon: "Users",
    module: "members",
    group: "operacion",
  },
  {
    name: "Reportes",
    href: "/reports",
    icon: "BarChart3",
    module: "reports",
    group: "operacion",
  },
  {
    name: "Especialidades",
    href: "/progress",
    icon: "Award",
    module: "progress",
    group: "actividades",
  },
  {
    name: "Eventos",
    href: "/events",
    icon: "Calendar",
    module: "events",
    group: "actividades",
  },
  {
    name: "Equipo",
    href: "/team",
    icon: "UserCog",
    module: "users",
    group: "gestion",
  },
  {
    name: "Configuración",
    href: "/settings",
    icon: "Settings",
    module: "settings",
    group: "gestion",
  },
];

/**
 * Etiquetas de grupo contextualizadas por rol.
 * Cada rol ve etiquetas que reflejan SU perspectiva de la organización.
 */
const NAV_GROUP_LABELS: Record<SystemRole, Record<string, string>> = {
  admin_union: {
    inicio: "Inicio",
    organizacion: "Organización",
    operacion: "Datos",
    actividades: "Actividades",
    gestion: "Gestión",
  },
  admin_association: {
    inicio: "Inicio",
    organizacion: "Mi Asociación",
    operacion: "Datos",
    actividades: "Actividades",
    gestion: "Gestión",
  },
  club_director: {
    inicio: "Inicio",
    organizacion: "Mi Club",
    operacion: "Gestión del Club",
    actividades: "Formación",
    gestion: "Más",
  },
  member: {
    inicio: "Inicio",
    organizacion: "Club",
    operacion: "Mi info",
    actividades: "Actividades",
    gestion: "Más",
  },
};

/**
 * Obtiene la etiqueta de grupo según el rol del usuario.
 */
export function getNavGroupLabel(
  role: SystemRole | undefined,
  groupKey: string,
): string {
  if (!role) return groupKey;
  return NAV_GROUP_LABELS[role]?.[groupKey] || groupKey;
}

/**
 * Obtiene los items de navegación filtrados por rol
 */
export function getNavItemsForRole(role: SystemRole | undefined): NavItem[] {
  if (!role) return [];
  return NAV_ITEMS.filter((item) => {
    if (!canAccessModule(role, item.module)) return false;
    if (item.roles && !item.roles.includes(role)) return false;
    return true;
  });
}

// ============================================
// AUTORIZACIÓN POR CLUB (scope-aware)
// ============================================

import type { Club, RoleAssignment } from "@/types";

/**
 * ¿El usuario puede gestionar (inscribir miembros, editar, etc.) este club?
 *
 * Reglas:
 *  - admin_union (scopeType=union)            → todos los clubs
 *  - admin_association (scopeType=association)→ clubs cuyo associationId == scopeId
 *  - club_director (scopeType=club)           → solo clubs cuyo id ∈ scopeIds
 *  - member                                   → ninguno
 *
 * NOTA: Filtrado de UX. La autoridad final la imponen las firestore.rules
 * (ver bug #12 fase 2 para endurecimiento server-side).
 */
export function canManageClub(
  club: Pick<Club, "id" | "associationId">,
  roleAssignments: RoleAssignment[] | undefined | null,
): boolean {
  if (!roleAssignments || roleAssignments.length === 0) return false;
  for (const ra of roleAssignments) {
    if (ra.role === "admin_union") return true;
    if (
      ra.role === "admin_association" &&
      ra.scopeType === "association" &&
      club.associationId &&
      ra.scopeId === club.associationId
    ) {
      return true;
    }
    if (
      ra.role === "club_director" &&
      ra.scopeType === "club" &&
      ra.scopeId === club.id
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Filtra una lista de clubs dejando solo los que el usuario puede gestionar.
 */
export function filterManageableClubs<
  C extends Pick<Club, "id" | "associationId">,
>(
  clubs: C[] | undefined | null,
  roleAssignments: RoleAssignment[] | undefined | null,
): C[] {
  if (!clubs) return [];
  if (!roleAssignments || roleAssignments.length === 0) return [];
  if (roleAssignments.some((ra) => ra.role === "admin_union")) return clubs;
  return clubs.filter((c) => canManageClub(c, roleAssignments));
}
